import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  UserPlusIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import axiosInstance from '../api/axiosInstance';
import logger from '../../utils/logger';

const PermissionManager = ({
  open,
  onClose,
  title,
  type,
  currentPermissions = [],
  onSave,
  businessId,
  userId,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [originalUsers, setOriginalUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [allUsers, setAllUsers] = useState([]);

  const getUserId = (user) => user.id || user._id;

  // Deduplicate and load current permissions
  useEffect(() => {
    const permissions = currentPermissions || [];
    const seen = new Set();
    const unique = permissions.filter(user => {
      const uid = getUserId(user);
      if (seen.has(uid)) return false;
      seen.add(uid);
      return true;
    });
    setSelectedUsers(unique);
    setOriginalUsers(unique);
  }, [currentPermissions]);

  // Load all users when modal opens
  useEffect(() => {
    if (!open) return;
    setSearchQuery('');
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get('/api/business/users', { params: { limit: 200, includeOwner: true } });
        setAllUsers(response.data.users || []);
      } catch (err) {
        logger.error('Error fetching users:', err);
        setError('Failed to load users');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [open]);

  // Filter available users
  useEffect(() => {
    const filtered = allUsers.filter(user => {
      if (selectedUsers.some(selected => getUserId(selected) === getUserId(user))) return false;
      if (getUserId(user) === userId) return false;
      if (searchQuery.length >= 1) {
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        const email = (user.email || '').toLowerCase();
        const query = searchQuery.toLowerCase();
        return fullName.includes(query) || email.includes(query);
      }
      return true;
    });
    setSearchResults(filtered);
  }, [searchQuery, selectedUsers, allUsers, userId]);

  const handleAddUser = (user) => {
    if (type === 'forwarding') {
      setSelectedUsers([user]);
    } else {
      if (!selectedUsers.some(selected => getUserId(selected) === getUserId(user))) {
        setSelectedUsers(prev => [...prev, user]);
      }
    }
    setSearchQuery('');
  };

  const handleRemoveUser = (id) => {
    setSelectedUsers(prev => prev.filter(user => getUserId(user) !== id));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      if (type?.startsWith('mailbox.')) {
        const permType = type.split('.')[1];
        const apiUrl = `/api/business/users/mailbox-permissions`;

        for (const user of selectedUsers) {
          await axiosInstance.post(apiUrl, {
            mailboxOwnerId: userId,
            grantedToUserId: getUserId(user),
            permissionType: permType
          });
        }

        const removedUsers = originalUsers.filter(
          orig => !selectedUsers.some(sel => getUserId(sel) === getUserId(orig))
        );
        for (const user of removedUsers) {
          try {
            await axiosInstance.delete(`/api/business/users/mailbox-permissions/${userId}/${getUserId(user)}/${permType}`);
          } catch (err) {
            logger.warn('Failed to revoke permission:', err);
          }
        }
      } else if (type?.startsWith('calendar.')) {
        const permType = type.split('.')[1];
        await axiosInstance.post(`/api/business/users/${userId}/permissions/calendar`, {
          permissionType: permType,
          userIds: selectedUsers.map(getUserId)
        });
      } else if (type === 'forwarding') {
        await axiosInstance.post(`/api/business/users/${userId}/permissions/forwarding`, {
          forwardingEnabled: selectedUsers.length > 0,
          forwardingAddress: selectedUsers.length > 0 ? selectedUsers[0].email : ''
        });
      }

      onSave?.(selectedUsers);
      onClose();
    } catch (err) {
      logger.error('Error saving permissions:', err);
      setError(err.response?.data?.message || 'Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    if (selectedUsers.length !== originalUsers.length) return true;
    return selectedUsers.some(sel => !originalUsers.some(orig => getUserId(orig) === getUserId(sel))) ||
           originalUsers.some(orig => !selectedUsers.some(sel => getUserId(sel) === getUserId(orig)));
  };

  if (!open) return null;

  const getInitials = (user) => {
    const first = user.firstName?.charAt(0) || '';
    const last = user.lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || '?';
  };

  const getAvatarColor = (user) => {
    const colors = [
      'from-orange-400 to-orange-600',
      'from-blue-400 to-blue-600',
      'from-emerald-400 to-emerald-600',
      'from-purple-400 to-purple-600',
      'from-rose-400 to-rose-600',
      'from-amber-400 to-amber-600',
      'from-cyan-400 to-cyan-600',
      'from-indigo-400 to-indigo-600',
    ];
    const name = (user.firstName || '') + (user.lastName || '');
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[10001] p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 w-full max-w-md max-h-[85vh] overflow-hidden flex flex-col animate-in fade-in zoom-in-95">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800/80">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center">
              <ShieldCheckIcon className="w-4.5 h-4.5 text-orange-500" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">{title}</h3>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                {type === 'forwarding' ? 'Select one user' : 'Add or remove user access'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-lg p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <XMarkIcon className="w-4.5 h-4.5" />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-5 mt-3 p-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-xs text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or email..."
              autoFocus
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 focus:outline-none bg-gray-50 dark:bg-[#222222] text-gray-900 dark:text-white placeholder-gray-400 transition-all"
            />
          </div>

          {/* Available Users */}
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-orange-500 border-t-transparent" />
              <span className="ml-2.5 text-xs text-gray-500 dark:text-gray-400">Loading users...</span>
            </div>
          ) : searchResults.length > 0 ? (
            <div className="max-h-44 overflow-y-auto rounded-lg border border-gray-100 dark:border-gray-800 divide-y divide-gray-50 dark:divide-gray-800/50">
              {searchResults.map((user) => (
                <button
                  key={getUserId(user)}
                  onClick={() => handleAddUser(user)}
                  className="w-full px-3 py-2.5 hover:bg-orange-50/60 dark:hover:bg-orange-900/10 transition-all text-left flex items-center gap-3 group"
                >
                  <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${getAvatarColor(user)} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                    <span className="text-[10px] font-semibold text-white">{getInitials(user)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate leading-tight">
                      {user.firstName} {user.lastName}
                    </p>
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate">{user.email}</p>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <UserPlusIcon className="w-4 h-4 text-orange-500" />
                  </div>
                </button>
              ))}
            </div>
          ) : allUsers.length > 0 && !loading ? (
            <div className="py-3 text-center">
              <p className="text-xs text-gray-400 dark:text-gray-500">
                {searchQuery ? 'No matching users found' : 'All users have been added'}
              </p>
            </div>
          ) : null}

          {/* Selected Users */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Granted Access
              </span>
              <span className="text-[11px] font-medium text-orange-500 bg-orange-50 dark:bg-orange-900/20 px-2 py-0.5 rounded-full">
                {selectedUsers.length} {selectedUsers.length === 1 ? 'user' : 'users'}
              </span>
            </div>

            {selectedUsers.length === 0 ? (
              <div className="py-6 text-center border border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
                <ShieldCheckIcon className="w-8 h-8 mx-auto text-gray-300 dark:text-gray-600 mb-1.5" />
                <p className="text-xs text-gray-400 dark:text-gray-500">No users have access</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {selectedUsers.map((user) => (
                  <div
                    key={getUserId(user)}
                    className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 dark:bg-[#222222] border border-gray-100 dark:border-gray-800 rounded-lg group hover:border-red-200 dark:hover:border-red-900/50 transition-all"
                  >
                    <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${getAvatarColor(user)} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                      <span className="text-[10px] font-semibold text-white">{getInitials(user)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate leading-tight">
                        {user.firstName} {user.lastName}
                      </p>
                      <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate">{user.email}</p>
                    </div>
                    <button
                      onClick={() => handleRemoveUser(getUserId(user))}
                      className="p-1 text-gray-300 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-400 transition-colors rounded-md hover:bg-red-50 dark:hover:bg-red-900/20"
                      title="Remove access"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3.5 border-t border-gray-100 dark:border-gray-800/80 bg-gray-50/50 dark:bg-[#111111]/30">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges()}
            className="flex items-center gap-1.5 px-5 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-all min-w-[110px] justify-center shadow-sm hover:shadow"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" />
            ) : (
              <>
                <CheckCircleIcon className="w-4 h-4" />
                Save
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PermissionManager;
